import { useCallback, useEffect, useMemo, useState } from "react"

type BuildStatusSource = "dev-runtime" | "build"

interface RuntimeGitResponse {
  commitHash?: string
  branchName?: string
  environment?: string
  refreshedAt?: string
}

export interface RuntimeBuildStatus {
  branch: string
  commit: string
  commitShort: string
  environment: string
  label: string
  refreshedAt?: string
  source: BuildStatusSource
}

const viteEnv = ((import.meta as any).env ?? {}) as Record<string, string | boolean | undefined>

const getDefinedGitCommit = () => (
  typeof __GIT_COMMIT__ !== "undefined" && __GIT_COMMIT__ ? __GIT_COMMIT__ : "dev"
)

const getDefinedGitBranch = () => (
  typeof __GIT_BRANCH__ !== "undefined" && __GIT_BRANCH__ ? __GIT_BRANCH__ : "local"
)

const normalizeCommit = (commit: string | undefined) => {
  if (!commit || commit === "unknown") return "dev"
  return commit
}

const getEnvironmentLabel = (environment: string, source: BuildStatusSource) => {
  if (source === "dev-runtime" || environment === "local" || environment === "development") {
    return "Local dev server"
  }
  if (environment === "staging") return "Staging build"
  if (environment === "production") return "Production build"
  return "Build status"
}

const getFallbackStatus = (): RuntimeBuildStatus => {
  const environment = String(viteEnv.VITE_ENVIRONMENT || viteEnv.VITE_ENV || "local")
  const commit = normalizeCommit(getDefinedGitCommit())
  const branch = getDefinedGitBranch()

  return {
    branch,
    commit,
    commitShort: commit.slice(0, 8),
    environment,
    label: getEnvironmentLabel(environment, "build"),
    source: "build",
  }
}

export function useRuntimeBuildStatus(refreshWhenOpen = false): RuntimeBuildStatus {
  const [status, setStatus] = useState<RuntimeBuildStatus>(() => getFallbackStatus())

  const refreshStatus = useCallback(async () => {
    if (!viteEnv.DEV) return

    try {
      const response = await fetch("/__orca/runtime-git-status", {
        cache: "no-store",
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return

      const runtimeStatus = (await response.json()) as RuntimeGitResponse
      const environment = runtimeStatus.environment || "local"
      const commit = normalizeCommit(runtimeStatus.commitHash)
      const branch = runtimeStatus.branchName || "local"

      setStatus({
        branch,
        commit,
        commitShort: commit.slice(0, 8),
        environment,
        label: getEnvironmentLabel(environment, "dev-runtime"),
        refreshedAt: runtimeStatus.refreshedAt,
        source: "dev-runtime",
      })
    } catch {
      // Keep deploy-time/build-time metadata if the dev-only endpoint is unavailable.
    }
  }, [])

  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  useEffect(() => {
    if (!refreshWhenOpen) return

    void refreshStatus()
    const interval = window.setInterval(() => void refreshStatus(), 10000)
    return () => window.clearInterval(interval)
  }, [refreshStatus, refreshWhenOpen])

  return useMemo(() => status, [status])
}
